import * as moment from 'moment';

import { AbstractControl, AbstractControlOptions, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { AfterViewInit, Component, ElementRef, EventEmitter, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';

import { Cargo } from '@app/core/models/questionario-cargo';
import { Concurso } from '@app/core/models/concurso';
import { FiltroQuestionarioCargo } from '../filtro-questionario-cargo';
import { MessageService } from 'primeng/api';
import { OnlyNumbersDirective } from '@core/directives/onlyNumbers.directive';
import { QuestionarioCargoApiService } from '@app/tabelasAdministrativas/services/questionario-cargo-api.service';
import { Subscription } from 'rxjs';
import { ValidateHelper } from '@app/core/helpers/validate.helper';

@Component({
	selector: 'sgp-pesquisar-questionario-cargo',
	templateUrl: './pesquisar-questionario-cargo.component.html',
	providers: [MessageService]
})
export class PesquisarQuestionarioCargoComponent implements OnInit, AfterViewInit, OnDestroy  {
	@Output() resultadoPesquisa: EventEmitter<any> = new EventEmitter<any>();
	@Output() atualizaConcurso: EventEmitter<Concurso> = new EventEmitter<Concurso>();
	@Output() filtrosPesquisa: EventEmitter<FiltroQuestionarioCargo> = new EventEmitter<FiltroQuestionarioCargo>();
	@ViewChild('elTextoPesquisa') elTextoPesquisa!: ElementRef;

	form!: FormGroup;
	submetido = false;
	loading = true;
	filtro = new FiltroQuestionarioCargo();
	concursoSelecionado?: Concurso;
	periodo: Date[] = [];
	placeholderConcurso = "Comece a escrever o nome do concurso para exibir as sugestões";
	placeholderOrgao = "Comece a escrever o nome do órgão para exibir as sugestões";
	placeholderCargo = "Busque aqui pelo nome do cargo";
	subscriptions: Subscription[] = [];
	mensagensErro: any[] = [];
	concursos: any[] =[];
	filteredConcursos: any[] = [];
	orgaos: any[] = [];
	filteredOrgaos: any[] = [];
	


	constructor(protected cargoApiService: QuestionarioCargoApiService, private fb: FormBuilder, private messageService: MessageService, private validate: ValidateHelper){}


	ngOnInit(): void {
		this.carregarCombos();
		this.criarFormulario();
		this.preencherDatas();
	}

	ngAfterViewInit(): void {
		this.elTextoPesquisa.nativeElement.focus();
	}

	ngOnDestroy(): void {
		this.subscriptions.forEach(subscription => subscription.unsubscribe());
		this.cargoApiService.concursos$ = undefined;
		this.cargoApiService.orgaos$ = undefined;
		this.cargoApiService.cargos$ = undefined;
	}

	pesquisar() {
		this.submetido = true;
		this.messageService.clear();
		if (this.form.valid) {
			this.preparaFiltro();
			if (!this.validarExistenciaDeParametrosParaPesquisa()) {
				this.messageService.add({
					severity: 'warn',
					summary: 'Para a pesquisa de cargos, deve-se preencher no mínimo um dos campos.'
				});
				return;
			}
			this.atualizaConcurso.emit(this.concursoSelecionado);
			this.filtrosPesquisa.emit(this.filtro);
			this.submetido = false;
		} else {
			this.form.markAllAsTouched();
		}
	}

	limpar() {
		this.submetido = false;
		this.form.reset();
		this.form.get('idConcurso')?.enable();
		this.filtro = new FiltroQuestionarioCargo();
		this.limparConcursoSelecionado();
		this.cargoApiService.orgaos$ = undefined;
		this.messageService.clear();
	}

	// onChange event no selecionar concurso:
	selecionarConcurso(event: any): void {
		const idConcurso = event?.value ? event.value.value : undefined;
		
		if (idConcurso != null) {
			this.form.get('codigoOrgao')?.reset();
			this.cargoApiService.carregarOrgaos(idConcurso);
			this.tratarDadosOrgao();
			this.subscriptions.push(
				this.cargoApiService.obterConcurso(idConcurso).subscribe(res => {
					this.concursoSelecionado = new Concurso(
						res.id,
						res.nome,
						res.utiliza_perfil,
						res.codigo,
						res.situacao
					);
				})
			);
		} else {
			if (this.concursos) {
				this.filteredConcursos = this.concursos;
			}
			this.limparConcursoSelecionado();
		}
		this.reiniciarPaginacao();
	}

	selecionarOrgao(event: any) {
		if (!event) {
			this.filteredOrgaos = this.orgaos;
		}
		this.reiniciarPaginacao();
	}

	limparConcursoSelecionado(): void {
		this.concursoSelecionado = undefined;
		this.cargoApiService.carregarOrgaos();
		this.tratarDadosOrgao();
	}

	reiniciarPaginacao() {
		this.filtro.page = 1;
	}

	filtrarConcursos(event: any) {
		const query = event.query;
		this.filteredConcursos = this.filtrarLista(query, 'label', this.concursos);
	}

	filtrarOrgaos(event: any) {
		const query = event.query;
		this.filteredOrgaos = this.filtrarLista(query, 'name', this.orgaos);
	}

	private filtrarLista(query: string, propFilter: string, listaOriginal: any[]): any[] {
		const filtered: any[] = [];

		if (listaOriginal) {
			for (const item of listaOriginal) {
				if (item[propFilter].toLowerCase().indexOf(query.toLowerCase()) != -1) {
					filtered.push(item);
				}
			}
		}
		return filtered;
	}

	get codigoCargoStr() {
		return this.form.get('codigoCargoStr');
	}

	formatarCodigoCargo() {
		let valor = this.codigoCargoStr?.value;
		if (valor && /^\d+$/.test(valor)) {
			let numero = parseInt(valor, 10);
			if (numero >= 0 && numero <= 999999) {
				let formatado = numero.toString().padStart(6, '0');
				this.codigoCargoStr?.setValue(formatado, { emitEvent: false });
				return;
			}
		}
		this.codigoCargoStr?.setValue('');
	}

	private carregarCombos() {
		if (!this.cargoApiService.concursos$) {
			try {
				this.cargoApiService.carregarConcursos();
				this.tratarDadosConcurso();
			} catch(error) {
				this.handleError(error, 'buscar concursos');
				throw error;
			}
		}
		this.cargoApiService.carregarOrgaos();
		this.tratarDadosOrgao();
		this.cargoApiService.carregarSituacoes();
	}

	private tratarDadosConcurso() {
		if (this.cargoApiService.concursos$) {
			this.subscriptions.push(
				this.cargoApiService.concursos$!.subscribe(res => {
				  this.concursos = this.cargoApiService.tratarDadosConcurso(res.items);
				  this.filteredConcursos = this.concursos;
				  this.loading = false;
				})
			);
		}
	}

	private tratarDadosOrgao() {
		if (this.cargoApiService.orgaos$) {
			this.subscriptions.push(
				this.cargoApiService.orgaos$!.subscribe(res => {
				  this.orgaos = this.cargoApiService.tratarDadosOrgao(res);
				  this.filteredOrgaos = this.orgaos;
				})
			  );
		}	
	}

	private criarFormulario() {
		this.form = this.fb.group({
			idConcurso: [this.filtro.idConcurso],
			codigoOrgao: [this.filtro.codigoOrgao],
			nomeCargo: [this.filtro.nomeCargo],
			codigoCargoStr: ['', Validators.pattern('^[0-9]+$')],
			situacao: [this.filtro.situacao],
			periodo: [this.periodo, [this.validarPeriodo]]
		} as AbstractControlOptions);
	}

	private validarPeriodo(control: AbstractControl): ValidationErrors | null {
		if (control.value) {
			const dataInicio = control.value[0];
			const dataFim = control.value[1];
			if (dataInicio && !dataFim) {
				return { dataFinalDePeriodoFaltando: true };
			}
		} 	 	
		return null;
	}


	private preencherDatas() {
		if (this.filtro.dataInicioLiberacao && this.filtro.dataFimLiberacao) {
			this.periodo = [
				this.prepararDataForm(this.filtro.dataInicioLiberacao),
				this.prepararDataForm(this.filtro.dataFimLiberacao)
			];
			this.form.get('periodo')?.setValue(this.periodo);
		}
	}

	private prepararDataForm(data?: string): Date {
		return moment(data, 'DD/MM/YYYY').toDate();
	}

	private preparaFiltro() {
		this.filtro.idConcurso = this.obterValor('idConcurso');
		this.filtro.codigoOrgao = this.obterValor('codigoOrgao');
		this.filtro.nomeCargo = this.obterValor('nomeCargo');
		this.filtro.codigoCargo = this.obterValor('codigoCargoStr');
		this.filtro.situacao = this.obterValor('situacao');
		if (this.obterValor('periodo') != null) {
			this.filtro.dataInicioLiberacao = this.prepararDataFiltro(this.obterValor('periodo')[0]);
			this.filtro.dataFimLiberacao = this.prepararDataFiltro(this.obterValor('periodo')[1]);
		}
	}

	private obterValor(campo: string): any {
		let valor = this.form.get(campo)?.value;
		if (campo == 'situacao' && valor == 'todos')
			return null;
		if (campo == 'codigoCargoStr') {
			valor = this.validate.isEmBranco(this.codigoCargoStr?.value) ? null : parseInt(this.codigoCargoStr?.value, 10);
			const codigoCargoNumber = valor > 0 ? valor : null;
			return codigoCargoNumber; 
		}
		return valor;
	}

	private prepararDataFiltro(data: Date): string | undefined {
		return data != null ? moment(data).format('DD/MM/YYYY') : undefined;
	}

	private validarExistenciaDeParametrosParaPesquisa(): boolean {

		return (  ( !this.validate.isEmBranco(this.filtro.nomeCargo) ) ||
				  ( this.filtro.idConcurso  != null ) || 
			      ( this.filtro.codigoOrgao != null ) || 
			      ( this.filtro.codigoCargo != null ) ||
			      ( this.filtro.situacao != null ) ||
			      ( this.filtro.dataInicioLiberacao != null ) ||
			      ( this.filtro.dataFimLiberacao != null ) );
	}


	private handleError(error: any, descricao: string) {
		this.messageService.add({
			severity: 'error',
			summary: `Erro ao ${descricao}. `,
			detail: ' Tente novamente mais tarde.'
		});
	}

}
